// Thin fetch wrapper. Reads the bearer token from localStorage and attaches it to every API
// call; surfaces 401s in a way the UI can detect so we can drop the bad token and re-prompt.
//
// All URLs are relative; in dev Vite proxies `/api/*` to the FastAPI backend (see vite.config.ts).
// In prod the FastAPI server itself serves the SPA so same-origin works.

import type {
  CallPathResponse,
  FeedbackAck,
  FeedbackDetail,
  FeedbackKind,
  FeedbackLogRow,
  FilePreview,
  FindFieldResolversResponse,
  FindFieldsLikeResponse,
  FindJsxUsagesResponse,
  FindOperationCallsitesResponse,
  FindOperationsLikeResponse,
  GraphqlOperationResponse,
  RepoRow,
  BranchRow,
  SearchFilters,
  SearchResponse,
} from './types';

export const TOKEN_KEY = 'code_rag_token';

// The browser's `storage` event only fires in *other* tabs. Dispatch this custom event so the
// current tab can react to its own token mutations too (see App.tsx).
export const TOKEN_CHANGE_EVENT = 'code-rag-token-change';

function notifyTokenChange(): void {
  try {
    window.dispatchEvent(new Event(TOKEN_CHANGE_EVENT));
  } catch {
    /* non-browser env (tests/SSR): ignore */
  }
}

export class UnauthorizedError extends Error {
  constructor(message = 'unauthorized') {
    super(message);
    this.name = 'UnauthorizedError';
  }
}

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* private-browsing or quota: ignore — the user will get re-prompted next reload */
  }
  notifyTokenChange();
}

export function clearToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
  notifyTokenChange();
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  if (!token) {
    throw new UnauthorizedError('no bearer token');
  }
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${token}`);
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const res = await fetch(path, { ...init, headers });
  if (res.status === 401) {
    clearToken();
    throw new UnauthorizedError();
  }
  if (!res.ok) {
    let detail = '';
    try {
      const body = await res.json();
      detail = body?.detail ?? body?.error ?? '';
    } catch {
      detail = res.statusText;
    }
    throw new Error(`${res.status} ${detail || res.statusText}`);
  }
  if (res.status === 204) {
    return undefined as T;
  }
  return (await res.json()) as T;
}

// --- Endpoints --- (one function per backend route, typed return)

export function listRepos(): Promise<{ repos: RepoRow[] }> {
  return request('/api/repos');
}

export function listBranches(repo: string): Promise<{ repo: string; branches: BranchRow[] }> {
  return request(`/api/branches?repo=${encodeURIComponent(repo)}`);
}

export function search(query: string, filters: SearchFilters, limit = 10): Promise<SearchResponse> {
  const params = new URLSearchParams({ q: query, limit: String(limit) });
  if (filters.repos.length) params.set('repos', filters.repos.join(','));
  if (filters.repo_kinds.length) params.set('repo_kinds', filters.repo_kinds.join(','));
  if (filters.languages.length) params.set('languages', filters.languages.join(','));
  if (filters.min_score > 0) params.set('min_score', String(filters.min_score));
  const branchPairs = Object.entries(filters.branches)
    .filter(([, branch]) => branch && branch !== 'main')
    .map(([repo, branch]) => `${repo}:${branch}`);
  if (branchPairs.length) params.set('branches', branchPairs.join(','));
  return request(`/api/search?${params}`);
}

export function getFile(args: {
  repo: string;
  path: string;
  branch?: string;
  start?: number;
  end?: number;
  context?: number;
}): Promise<FilePreview> {
  const params = new URLSearchParams({
    repo: args.repo,
    path: args.path,
    branch: args.branch ?? 'main',
    start: String(args.start ?? 1),
    end: String(args.end ?? args.start ?? 1),
  });
  if (args.context !== undefined) params.set('context', String(args.context));
  return request(`/api/file?${params}`);
}

export function lookupOperation(name: string): Promise<GraphqlOperationResponse> {
  return request(`/api/op/${encodeURIComponent(name)}`);
}

export function findFieldResolvers(
  type: string,
  field: string,
): Promise<FindFieldResolversResponse> {
  return request(
    `/api/field-resolvers?type=${encodeURIComponent(type)}&field=${encodeURIComponent(field)}`,
  );
}

export function findFieldsLike(
  type: string,
  pattern: string,
): Promise<FindFieldsLikeResponse> {
  return request(
    `/api/fields-like?type=${encodeURIComponent(type)}&pattern=${encodeURIComponent(pattern)}`,
  );
}

export function findOperationsLike(pattern: string): Promise<FindOperationsLikeResponse> {
  return request(`/api/operations-like?pattern=${encodeURIComponent(pattern)}`);
}

export function findOperationCallsites(name: string): Promise<FindOperationCallsitesResponse> {
  return request(`/api/callsites/${encodeURIComponent(name)}`);
}

export function findJsxUsages(identifier: string): Promise<FindJsxUsagesResponse> {
  return request(`/api/jsx-usages/${encodeURIComponent(identifier)}`);
}

export function callPath(args: {
  from: string;
  to: string;
  maxDepth?: number;
  viaGraphql?: boolean;
  branches?: Record<string, string>;
}): Promise<CallPathResponse> {
  const params = new URLSearchParams({
    from: args.from,
    to: args.to,
    max_depth: String(args.maxDepth ?? 6),
    via_graphql: String(args.viaGraphql ?? true),
  });
  if (args.branches) {
    const pairs = Object.entries(args.branches)
      .filter(([, b]) => b && b !== 'main')
      .map(([r, b]) => `${r}:${b}`);
    if (pairs.length) params.set('branches', pairs.join(','));
  }
  return request(`/api/call-path?${params}`);
}

export function postFeedback(kind: FeedbackKind, payload: Record<string, unknown>): Promise<FeedbackAck> {
  return request('/api/feedback', {
    method: 'POST',
    body: JSON.stringify({ kind, payload }),
  });
}

export function listFeedback(args: {
  since?: string;
  kind?: string;
  status?: string;
  limit?: number;
} = {}): Promise<{ feedback: FeedbackLogRow[] }> {
  const params = new URLSearchParams();
  if (args.since) params.set('since', args.since);
  if (args.kind) params.set('kind', args.kind);
  if (args.status) params.set('status', args.status);
  if (args.limit) params.set('limit', String(args.limit));
  const qs = params.toString();
  return request(`/api/feedback${qs ? `?${qs}` : ''}`);
}

/** One feedback row plus its linked tool_call — the tool, its correlation id, and the
 *  result snapshot the agent saw. Backs the expandable rows in the feedback log. */
export function getFeedbackDetail(id: number): Promise<FeedbackDetail> {
  return request(`/api/feedback/${id}`);
}

// --- Indexer ops dashboard --- (mirror the IndexRunRow / IndexStatsResponse / QueuesResponse
// Pydantic models in indexer/src/code_rag/web_server.py — keep the shapes in lockstep.)

/** Live SQS queue depths + ECS task counts for the dashboard "Now" panel. */
export interface QueuesResponse {
  queues: Record<string, { visible: number; in_flight: number }>;
  services: Record<string, { running: number; desired: number }>;
  dlq_nonempty: boolean;
}

/** Aggregates over code_rag_index_runs in a window: counts, phase timing, throughput,
 *  latency percentiles, and the headline push->searchable SLO. */
export interface IndexStatsResponse {
  window: string;
  status_counts: Record<string, number>;
  device_counts: Record<string, number>;
  phase_avg_ms: Record<string, number>;
  throughput: { bucket: string; jobs: number; emb_per_sec_avg: number }[];
  p50_queue_ms: number | null;
  p95_queue_ms: number | null;
  p50_e2e_ms: number | null;
  p95_e2e_ms: number | null;
  slo_pct_under_5min: number;
}

/** One code_rag_index_runs row, projected for the dashboard history table. */
export interface IndexRun {
  id: number;
  ts: string;
  repo: string | null;
  branch: string | null;
  mode: string | null;
  device: string | null;
  status: string | null;
  files_indexed: number | null;
  chunks_written: number | null;
  emb_per_sec: number | null;
  phase_walk_ms: number | null;
  phase_ast_ms: number | null;
  phase_embed_ms: number | null;
  phase_db_write_ms: number | null;
  elapsed_s: number | null;
  time_in_queue_ms: number | null;
  end_to_end_ms: number | null;
  receive_count: number | null;
}

export function getQueues(): Promise<QueuesResponse> {
  return request('/api/queues');
}

export function getIndexStats(window = '24h'): Promise<IndexStatsResponse> {
  return request(`/api/index-stats?window=${encodeURIComponent(window)}`);
}

export function getIndexRuns(limit = 50): Promise<{ runs: IndexRun[] }> {
  return request(`/api/index-runs?limit=${limit}`);
}
